import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Star } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

export default function FeedbackPage() {
    const { t } = useTranslation();
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [comment, setComment] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (rating === 0) return; // Prevent submission without a rating

        const feedbackData = {
            rating,
            comment,
            timestamp: new Date().toISOString()
        };

        // Store in localStorage
        localStorage.setItem('synthconst_feedback', JSON.stringify(feedbackData));

        // Switch view to Thank You state
        setSubmitted(true);
    };

    return (
        <div className="container mx-auto px-4 py-16 flex items-center justify-center min-h-[calc(100vh-14rem)]">
            <Card className="w-full max-w-md" data-testid="feedback-card">
                {submitted ? (
                    <CardContent className="pt-8 pb-8 text-center space-y-6">
                        <div className="flex justify-center mb-4">
                            <div className="h-16 w-16 bg-accent/20 rounded-full flex items-center justify-center">
                                <Star className="h-8 w-8 text-accent fill-accent" />
                            </div>
                        </div>
                        <CardTitle className="text-2xl" style={{ fontFamily: 'var(--font-heading)' }}>
                            {t('feedback.thankYou')}
                        </CardTitle>
                        <div className="pt-6 border-t mt-6">
                            <p className="text-muted-foreground mb-4">
                                {t('feedback.exploreMore')}
                            </p>
                            <a
                                href="https://alphasynthai.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-accent hover:underline font-medium"
                            >
                                alphasynthai.com
                            </a>
                        </div>
                    </CardContent>
                ) : (
                    <>
                        <CardHeader className="text-center pb-2">
                            <CardTitle className="text-2xl" style={{ fontFamily: 'var(--font-heading)' }}>
                                {t('feedback.title')}
                            </CardTitle>
                            <CardDescription className="text-base mt-2">
                                {t('feedback.subtitle')}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-6 pt-4">
                                {/* Star Rating System */}
                                <div className="flex justify-center space-x-2 my-6">
                                    {[1, 2, 3, 4, 5].map((starValue) => {
                                        const isFilled = starValue <= (hoverRating || rating);
                                        return (
                                            <button
                                                key={starValue}
                                                type="button"
                                                className="p-1 transition-transform hover:scale-110 focus:outline-none"
                                                onMouseEnter={() => setHoverRating(starValue)}
                                                onMouseLeave={() => setHoverRating(0)}
                                                onClick={() => setRating(starValue)}
                                            >
                                                <Star
                                                    className={`h-10 w-10 transition-colors ${isFilled
                                                        ? 'text-amber-500 fill-amber-500'
                                                        : 'text-muted-foreground/30 stroke-[1.5]'
                                                        }`}
                                                />
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Optional Feedback Textarea */}
                                <div className="space-y-2">
                                    <Textarea
                                        placeholder={t('feedback.placeholder')}
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                        className="min-h-[120px] resize-none"
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full"
                                    disabled={rating === 0}
                                >
                                    {t('feedback.submit')}
                                </Button>
                            </form>
                        </CardContent>
                    </>
                )}
            </Card>
        </div>
    );
}
